import './assets/styles/carousel.css'
import 'svgs/chevronLeft.svg'
import 'svgs/chevronRight.svg'

import Svg from '@capsule/svg'
import type { ReactElement } from 'jsx-dom-cjs'
import validateTarget from 'shared/assets/scripts/validate-target'

const DELAY_BEFORE_BUTTONS_UPDATE = 500 // Just above duration as the CSS transition

/**
 * To use this helper, you have to init the carousel specifying an element containing the carousel
 * and svg names for prev and next buttons
 * Example :
 * const carousel = new Carousel({
 *       element: document.getElementById('testCarousel'),
 *       svgLeft: 'caretLeft',
 *       svgRight: 'caretRight'
 *   })
 *   carousel.init()
 * You then specify a data-attribute on the carousel DOM Element, example:
 * <ul data-carousel-target><li>........</ul>
 */

export default class Carousel {
	private element: HTMLElement | null
	private svgLeft: string
	private svgRight: string
	private carousel: HTMLElement
	private carouselPosition: {
		left: number
		right: number
	}

	private carouselItems: HTMLElement[]
	private prevButton: HTMLElement | null
	private nextButton: HTMLElement | null
	private itemsToScrollBy: number
	private scrollByValue: number

	private debounceTimer: ReturnType<typeof setTimeout> | null
	private cachedPositions: { first: number; last: number } | null

	constructor({
		element,
		svgLeft,
		svgRight,
		itemsToScrollBy = 2
	}: {
		element: HTMLElement
		svgLeft?: string
		svgRight?: string
		itemsToScrollBy: number
	}) {
		this.element = element
		this.svgLeft = svgLeft ?? 'chevronLeft'
		this.svgRight = svgRight ?? 'chevronRight'
		this.itemsToScrollBy = itemsToScrollBy
		this.scrollByValue = 0
		this.carousel = this.element?.querySelector('[data-carousel-target]') as HTMLElement
		this.carouselPosition = {
			left: 0,
			right: 0
		}
		this.carouselItems = (
			this.carousel?.children ? Array.from(this.carousel.children) : []
		) as HTMLElement[]
		this.prevButton = null
		this.nextButton = null
		this.debounceTimer = null
		this.cachedPositions = null

		this.onClickNavigation = this.onClickNavigation.bind(this)
	}

	init() {
		if (!this.isCarouselNeeded()) {
			return
		}
		this.setCarouselAttributes()
		this.setCarouselItemsAttributes()
		this.createNavigationButtons()

		const gap = this.calculateGapFromLayout()
		this.scrollByValue = (this.carouselItems[0].offsetWidth + gap) * this.itemsToScrollBy

		this.updateItemsPositionData()
		this.addEvents()
	}

	isCarouselNeeded() {
		return this.carousel.scrollWidth > this.carousel.clientWidth
	}

	/**
	 * Calculate gap from layout without using getComputedStyle
	 * @returns {number} The gap value in pixels
	 */
	calculateGapFromLayout(): number {
		const firstItem = this.carouselItems[0]
		const secondItem = this.carouselItems[1]

		const firstRect = firstItem.getBoundingClientRect()
		const secondRect = secondItem.getBoundingClientRect()

		const gap = secondRect.left - firstRect.right

		return gap > 0 ? gap : 0
	}

	/**
	 * Update items position data
	 */
	updateItemsPositionData() {
		const boundingClientRect = this.carousel.getBoundingClientRect()
		this.carouselPosition.left = boundingClientRect.left
		this.carouselPosition.right = boundingClientRect.right
	}

	/**
	 * Add event listeners
	 */
	addEvents() {
		this.element?.addEventListener('click', this.onClickNavigation)
	}

	/**
	 * On click navigation actions
	 * @param {object} event Event listener data
	 */
	onClickNavigation(event: MouseEvent) {
		const target = event.target as HTMLElement
		const isPrevTarget = validateTarget({
			target,
			selectorString: '.carousel-nav.prev',
			nodeName: ['div']
		})
		const isNextTarget = validateTarget({
			target,
			selectorString: '.carousel-nav.next',
			nodeName: ['div']
		})

		if (isPrevTarget) {
			this.handleNavigationClick('prev')
		} else if (isNextTarget) {
			this.handleNavigationClick('next')
		}
	}

	/**
	 * Handle navigation click
	 * @param {string} direction Direction to scroll towards
	 */
	handleNavigationClick(direction: 'next' | 'prev') {
		const directionMultiplier = direction === 'next' ? 1 : -1
		const newPosition = this.carousel.scrollLeft + this.scrollByValue * directionMultiplier

		this.carousel.scrollTo({
			left: newPosition,
			behavior: 'smooth'
		})

		// Debounce to avoid multiple calls
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer)
		}

		this.debounceTimer = setTimeout(() => {
			this.cachedPositions = null
			this.updateButtonsStatuses()
			this.debounceTimer = null
		}, DELAY_BEFORE_BUTTONS_UPDATE)
	}

	setCarouselAttributes() {
		this.element?.setAttribute('data-carousel', '')
		this.carousel?.classList.add('carousel-list')
		this.carousel?.setAttribute('aria-live', 'polite')
		this.carousel?.setAttribute('role', 'tablist')
	}

	setCarouselItemsAttributes() {
		const lengthItem = this.carouselItems?.length
		this.carouselItems?.forEach((item, index) => {
			item.classList.add('carousel-item')
			item.setAttribute('role', 'tab')
			item.setAttribute('aria-roledescription', 'slide')
			item.setAttribute('aria-label', `${index + 1} sur ${lengthItem}`)
		})
	}

	createNavigationButtons() {
		const fragment = document.createDocumentFragment()

		const prevButton = this.createButton({
			type: 'prev',
			title: 'Slide précédente',
			svg: Svg({ name: this.svgLeft })
		}) as Element

		const nextButton = this.createButton({
			type: 'next',
			title: 'Slide suivante',
			svg: Svg({ name: this.svgRight })
		}) as Element

		fragment.appendChild(prevButton)
		fragment.appendChild(nextButton)

		this.element?.insertBefore(fragment, this.element.firstChild)

		this.prevButton = this.element?.querySelector('.carousel-nav.prev') ?? null
		this.nextButton = this.element?.querySelector('.carousel-nav.next') ?? null
	}

	createButton({ type, title, svg }: { type: string; title: string; svg: ReactElement }) {
		const button = <button type="button" title={title} className={`carousel-button ${type}`} />
		const zone = (
			<div className={`carousel-nav ${type} ${type === 'prev' ? ' hide' : ''}`}>{button}</div>
		)
		button.appendChild(svg)

		return zone
	}

	/**
	 * Update buttons' statuses
	 */
	updateButtonsStatuses() {
		const firstCardPositionX =
			this.carouselItems[0].getBoundingClientRect().left - this.carouselPosition.left
		const lastCardPositionX =
			this.carouselItems[this.carouselItems.length - 1].getBoundingClientRect().right -
			this.carouselPosition.right

		// Cache the positions if they haven't changed significantly
		if (
			!this.cachedPositions ||
			Math.abs(this.cachedPositions.first - firstCardPositionX) > 0.5 ||
			Math.abs(this.cachedPositions.last - lastCardPositionX) > 0.5
		) {
			this.cachedPositions = { first: firstCardPositionX, last: lastCardPositionX }

			// Use the cache to avoid repeated querySelector
			const prevBtn = this.prevButton
			const nextBtn = this.nextButton

			Math.abs(firstCardPositionX) <= 32
				? prevBtn?.classList.add('hide')
				: prevBtn?.classList.remove('hide')

			Math.abs(lastCardPositionX) <= 32
				? nextBtn?.classList.add('hide')
				: nextBtn?.classList.remove('hide')
		}
	}
}
